import type { ReactNode } from 'react';

import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface Props {
  title: ReactNode;
  description: ReactNode;
  /** Header actions, e.g. an Add button. */
  action?: ReactNode;
  /** false: the card grows with its content instead of scrolling inside 360px. */
  fixedHeight?: boolean;
  children: ReactNode;
}

/**
 * One of the page's three equal, near-square cards (upload, supplier layouts,
 * validation rules). The height is fixed so the row stays even; the body
 * scrolls inside the card when its content is longer.
 */
export function ToolCard({
  title,
  description,
  action,
  fixedHeight = true,
  children,
}: Props) {
  return (
    <Card
      className={cn(
        'flex min-w-0 flex-col gap-2 p-3',
        fixedHeight && 'h-[360px]',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-sm font-medium">{title}</h2>
          <p className="text-muted-foreground text-xs">{description}</p>
        </div>
        {action ? <div className="flex shrink-0 gap-1">{action}</div> : null}
      </div>
      <div className={cn('min-h-0 flex-1', fixedHeight && 'overflow-y-auto')}>
        {children}
      </div>
    </Card>
  );
}
